import React from 'react'
import { renderToString } from 'react-dom/server'
import style from './style'
import injectSheet from 'react-jss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CameraDetailsContainer from './CameraDetailsContainer'
import ManufacturerList from './ManufacturerList'
import CameraList from './CameraList'
import Filter from './Filter'
import { connect } from 'react-redux'
import { changeCameraModelAction, removeCameraModelAction } from 'actions/cameraActions'
import { resetSpecificManufacturerAndRestartAppAction } from 'actions/applicationActions'
import { updateUiAction } from 'actions/uiActions'
import getCurrentCamera from 'helpers/cameras/getCurrentCamera'
import uniqBy from 'ramda/src/uniqBy'
import sort from 'ramda/src/sort'
import prop from 'ramda/src/prop'
import pipe from 'ramda/src/pipe'
import map from 'ramda/src/map'
import reduce from 'ramda/src/reduce'
import max from 'ramda/src/max'
import min from 'ramda/src/min'
import reject from 'ramda/src/reject'
import isNil from 'ramda/src/isNil'
import props from 'ramda/src/props'
import flatten from 'ramda/src/flatten'
import equals from 'ramda/src/equals'
import uniq from 'ramda/src/uniq'
import isEmpty from 'ramda/src/isEmpty'
import complement from 'ramda/src/complement'
import filter from 'ramda/src/filter'
import range from 'ramda/src/range'
import aovDecimalRange from 'helpers/aovDecimalRange'
import firstCamerasManufacturer from 'helpers/firstCamerasManufacturer'
import Fuse from 'fuse.js'
import { RECENT_DISPLAY_COUNT } from 'helpers/globals'
import sortBy from 'ramda/src/sortBy'
import tippy from 'tippy.js'
import { recentModelsSelector } from 'selectors/components'

const mapStateToProps = state => {
  const { cameras, cameraModels, cameraFilters, favoriteModels = [], manufacturerSpecificCameraModels, ui: { hideDiscontinuedModels, hideNdaaNonCompliantModels }, permalink } = state
  const model = getCurrentCamera(cameras).model

  return {
    model,
    cameraModels,
    manufacturerSpecificCameraModels,
    favoriteModels,
    recentModels: recentModelsSelector(state),
    cameraFilters,
    currentModel: model && cameraModels.find(m => m.id === model.id),
    hideDiscontinuedModels,
    hideNdaaNonCompliantModels,
    permalink
  }
}

const formFactors = ['box', 'bullet', 'covert', 'cube', 'dome', 'ptz', 'turret', 'multi-imager']
const aovRange = [ ...range(1, 181), 360 ]

class CameraModelSelector extends React.Component {
  constructor(props) {
    super(props)
    const { currentModel, cameraModels, favoriteModels, recentModels, manufacturerSpecificCameraModels, hideDiscontinuedModels, hideNdaaNonCompliantModels } = this.props
    const models = manufacturerSpecificCameraModels || cameraModels

    this.normalizedCameras = this.normalizeCameras(models.filter(c => !c.replaced))
    const options = {
      threshold: 0.22,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      shouldSort: true,
      includeScore: true,
      keys: [{
        name: 'trimmedModel',
        weight: 0.9
      }, {
        name: 'trimmedText',
        weight: 0.1
      }]
    }
    this.fuse = new Fuse(this.normalizedCameras, options)
    this.orderedResolutions = this.createOrderedResolutions()
    this.orderedInfrareds = this.orderInfrareds(this.normalizedCameras)
    this.initialFilters = {
      formFactors: [],
      resolutions: { lowI: 0, highI: this.orderedResolutions.length - 1 },
      ir: this.irRange(this.normalizedCameras, this.orderedInfrareds),
      aovRange,
      hideDiscontinued: hideDiscontinuedModels,
      hideNonCompliant: hideNdaaNonCompliantModels
    }

    let filteredCameras = this.normalizedCameras
    if (hideDiscontinuedModels) filteredCameras = filteredCameras.filter(c => !c.discontinued)
    if (hideNdaaNonCompliantModels) filteredCameras = filteredCameras.filter(c => c.ndaa_compliant)

    const selectedManufacturer = manufacturerSpecificCameraModels ? firstCamerasManufacturer(manufacturerSpecificCameraModels) : (currentModel ? currentModel.manufacturer : null)
    const cameras = selectedManufacturer ? filteredCameras.filter(c => c.manufacturer === selectedManufacturer) : filteredCameras
    const selectedCamera = currentModel && ((currentModel.discontinued && hideDiscontinuedModels) || currentModel.replaced) ? null : currentModel

    this.state = {
      selectedManufacturer,
      selectedCamera,
      search: '',
      cameras: sortBy(c => recentModels.indexOf(c.id) === -1 ? RECENT_DISPLAY_COUNT + 1 : recentModels.indexOf(c.id))(cameras),
      manufacturers: uniq(cameras.map(c => c.manufacturer)),
      filters: this.initialFilters,
      favoriteModels: favoriteModels || [],
      recentModels: recentModels
    }
  }

  normalizeCameras = cameras => {
    return cameras.map(c => {
      return {
        ...c,
        totalPixels: c.resolution.split(/x/i)[0] * c.resolution.split(/x/i)[1],
        aovRange: isEmpty(c.aovs) ? [] : this.gatherAovs(c),
        trimmedText: c.text.replace(/\s|\\|\)|\(|\[|-/g, ''),
        trimmedModel: c.model.replace(/\s|\\|\)|\(|\[|-/g, '')
      }
    })
  }

  gatherAovs =
    pipe(
      prop('aovs'),
      map(props(['min', 'max'])),
      flatten,
      reject(isNil),
      map(parseFloat)
    )

  orderResolutions =
    pipe(
      map(({ totalPixels, resolution_name }) => ({
        totalPixels,
        name: resolution_name
      })),
      sort((a, b) => a.totalPixels - b.totalPixels),
      uniqBy(c => c.totalPixels),
      filter(c => this.props.cameraFilters.resolutions
                    .map(prop('name'))
                    .includes(c.name)
            )
    )

  createOrderedResolutions = _ => {
    const resolutions = this.orderResolutions(this.normalizedCameras)
    return [
      { ...resolutions[0], name: 'Any' },
      ...resolutions.slice(1, -1),
      { ...resolutions[resolutions.length - 1], name: 'Any' }
    ]
  }

  irRange = (cameras, infraredVals) => {
    return {
      max: reduce(max, 0, infraredVals),
      min: reduce(min, 0, infraredVals)
    }
  }

  orderInfrareds =
    pipe(
      map(({ infrared }) => ({ infrared })),
      sort((a, b) => a.infrared - b.infrared),
      uniqBy(prop('infrared')),
      map(prop('infrared'))
    )

  updateCameraList = selectedCamera =>
    this.setState({ selectedCamera })

  aovRangeHasChanged = filters =>
    !equals(filters.aovRange, this.initialFilters.aovRange)

  resolutionRangeHasChanged = filters =>
    filters.resolutions.highI !== this.initialFilters.resolutions.highI ||
    filters.resolutions.lowI !== this.initialFilters.resolutions.lowI

  filtersAreInitial = (filters = this.state.filters) =>
    filters.resolutions.highI === this.initialFilters.resolutions.highI &&
    filters.resolutions.lowI === this.initialFilters.resolutions.lowI &&
    filters.ir.min === this.initialFilters.ir.min &&
    filters.ir.max === this.initialFilters.ir.max &&
    equals(filters.aovRange, this.initialFilters.aovRange) &&
    (filters.formFactors.length === formFactors.length || filters.formFactors.length === 0)

  filterCameras = (filterChanges, searchText = null, selectedManufacturer, unselectManufacturer = false) => {
    const selectedManuf = selectedManufacturer || this.state.selectedManufacturer
    const filters = {
      ...this.state.filters,
      ...filterChanges
    }

    const search = searchText === null ? this.state.search : searchText
    const sanitizedSearch = search.replace(/\s|\\|\)|\(|\[|-/g, '')
    const pixelValues = this.orderedResolutions.slice(filters.resolutions.lowI, filters.resolutions.highI + 1).map(prop('totalPixels'))
    const decimalRange = aovDecimalRange(filters.aovRange)

    let cameras = this.normalizedCameras

    if (complement(isEmpty)(sanitizedSearch)) {
      const results = this.fuse.search(sanitizedSearch)

      if (filters.hideDiscontinued && filters.hideNonCompliant) {
        cameras = results.filter(r => r.score < 0.002 && sanitizedSearch.length > 4 || (!r.item.discontinued && r.item.ndaa_compliant)).map(r => r.item)
      } else if (filters.hideDiscontinued) {
        cameras = results.filter(r => r.score < 0.002 && sanitizedSearch.length > 4 || !r.item.discontinued).map(r => r.item)
      } else if (filters.hideNonCompliant) {
        cameras = results.filter(r => r.score < 0.002 && sanitizedSearch.length > 4 || r.item.ndaa_compliant).map(r => r.item)
      } else {
        cameras = results.map(r => r.item)
      }
    } else if (filters.hideDiscontinued && filters.hideNonCompliant) {
      cameras = cameras.filter(c => !c.discontinued).filter(c => c.ndaa_compliant)
    } else if (filters.hideDiscontinued) {
      cameras = cameras.filter(c => !c.discontinued)
    } else if (filters.hideNonCompliant) {
      cameras = cameras.filter(c => c.ndaa_compliant)
    }

    if (complement(isEmpty)(filters.formFactors)) {
      cameras = cameras.filter(c => filters.formFactors.includes((c.form_factor || '').toLowerCase()))
    }

    if (this.resolutionRangeHasChanged(filters)) {
      cameras = cameras.filter(c => pixelValues.includes(c.totalPixels))
    }

    cameras = cameras.filter(c => c.infrared >= filters.ir.min && c.infrared <= filters.ir.max)

    if (this.aovRangeHasChanged(filters)) {
      cameras = cameras.filter(c => complement(isEmpty)(c.aovRange) && c.aovRange.some(a => decimalRange.includes(a)))
    }

    // to ensure selecting / deselecting a manufacturer doesn't hide the others, get manufacturers now
    const manufacturers = uniq(cameras.map(c => c.manufacturer))

    if (selectedManuf && !unselectManufacturer) {
      if (selectedManuf === 'Favorite') {
        cameras = cameras.filter(c => this.state.favoriteModels.includes(c.id))
      } else if (selectedManuf === 'Recent') {
        cameras = cameras.filter(c => this.state.recentModels.includes(c.id))
      } else {
        cameras = cameras.filter(c => c.manufacturer === selectedManuf)
      }
    }

    this.setState({
        filters,
        search,
        selectedManufacturer: unselectManufacturer ? null : selectedManuf,
        cameras: sortBy(c => this.state.recentModels.indexOf(c.id) === -1 ? RECENT_DISPLAY_COUNT + 1 : this.state.recentModels.indexOf(c.id))(cameras),
        selectedCamera: null,
        manufacturers
      })
  }

  toggleEolModels = value => {
    this.filterCameras({ hideDiscontinued: value }, null, null)
    this.props.updateUiAction({ hideDiscontinuedModels: value })
  }

  toggleNdaaCompliantModels = value => {
    this.filterCameras({ hideNonCompliant: value }, null, null)
    this.props.updateUiAction({ hideNdaaNonCompliantModels: value })
  }

  getDisplayModelCount = _ => {
    const { state: { search, selectedManufacturer, filters, cameras }, props: { manufacturerSpecificCameraModels, cameraModels }, filtersAreInitial } = this

    if (search.replace(/\s|\\|\)|\(|\[|-/g, '').length === 0 && !selectedManufacturer && !filters.hideDiscontinued && !filters.hideNonCompliant && filtersAreInitial()) {
      // return total count
      return (manufacturerSpecificCameraModels || cameraModels).length
    } else {
      return cameras.length
    }
  }

  componentWillReceiveProps({ favoriteModels, recentModels }) {
    this.setState({
      favoriteModels,
      recentModels
    })
  }

  componentDidMount = _ => {
    this.refs.searchInput.focus()
    tippy(this.refs.eolTag, { duration: 0, theme: 'topbar', placement: 'bottom', onShow: instance => this.tooltip = instance })
    tippy('#ndaaTag', { duration: 0, allowTitleHTML: true, delay: [0, 250], interactive: true, theme: 'topbar', placement: 'bottom', onShow: instance => this.tooltip = instance })
  }

  componentWillUnmount = _ => this.tooltip && this.tooltip.hide()

  viewChoiceButton = (choice, ndaa = false) => {
    const { hideNdaaNonCompliantModels, hideDiscontinuedModels, classes } = this.props
    const isSelected = choice.value === (ndaa ? hideNdaaNonCompliantModels : hideDiscontinuedModels)
    const toggle = ndaa ? this.toggleNdaaCompliantModels : this.toggleEolModels

    return (
      <div
        onClick={_ => !isSelected && toggle(choice.value)}
        className={classes.viewChoice + (isSelected ? ' selected' : '')}
        key={choice.text}
      >
        {choice.text}
      </div>
    )
  }

  ndaaHelpText = _ => (
    <span>
      Products that are produced by covered companies or have covered components as defined by NDAA FY 2019 Sec 889.
      <a href="https://ipvm.com/reports/ndaa-guide" target="_blank"> Read our guide here</a>
    </span>
  )

  render() {
    const { updateCameraList, filterCameras, orderedResolutions, orderedInfrareds, initialFilters, filtersAreInitial, viewChoiceButton, getDisplayModelCount,
      state: { manufacturers, selectedManufacturer, selectedCamera, search, cameras, filters, favoriteModels, recentModels },
      props: { removeCameraModelAction, manufacturerSpecificCameraModels, changeCameraModelAction, model, permalink, classes, resetSpecificManufacturerAndRestartAppAction } } = this

    const EOL_CHOICES = [
      { text: 'All Cameras', value: false },
      { text: 'Exclude EOL', value: true },
    ]
    const NDAA_CHOICES = [
      { text: 'All Cameras', value: false },
      { text: 'Only NDAA', value: true },
    ]

    return (
      <div className={classes.modelSelector}>
        <div className={classes.topElements}>
          <div className={classes.searchWrapper}>
            <input
              type='text'
              className={classes.search}
              placeholder={selectedManufacturer ? `Search ${selectedManufacturer}...` : 'Search...'}
              onChange={({ target: { value } }) => filterCameras({}, value)}
              value={search}
              ref='searchInput'
            />
          </div>
          <div className={classes.container}>
            <div className={classes.firstRow}>
              <span title='End Of Life, cameras that have been discontinued or are no longer produced.' ref='eolTag'>EOL:</span>
              <div className={classes.inputWrapper}>
                { EOL_CHOICES.map(choice => viewChoiceButton(choice)) }
              </div>
              <span title={renderToString(this.ndaaHelpText())} id='ndaaTag'>NDAA:</span>
              <div className={classes.inputWrapper}>
                { NDAA_CHOICES.map(choice => viewChoiceButton(choice, true)) }
              </div>
              </div>
          </div>
          <div className={classes.genericButton}>
            <button onClick={removeCameraModelAction} disabled={!model}>
              <FontAwesomeIcon icon='video' />
              Select Generic Camera
            </button>
          </div>
        </div>
        <Filter {...{ filterCameras, formFactors, initialFilters, filters, orderedResolutions, orderedInfrareds, aovRange }} />
        <div className={classes.columnWrapper}>
          <ManufacturerList {...{ manufacturers, search, selectedManufacturer, filterCameras,
            manufacturerSpecificCameraModels, resetSpecificManufacturerAndRestartAppAction }}
          />
          <CameraList {...{ cameras, selectedCamera, updateCameraList, favoriteModels, recentModels,
            filtersAreInitial, search, selectedManufacturer, manufacturers, filterCameras }} />
          <CameraDetailsContainer
            {...{ selectedCamera, changeCameraModelAction, filters, permalink,
              orderedResolutions, selectedManufacturer, manufacturerSpecificCameraModels }}
            modelCount={getDisplayModelCount()}
          />
        </div>
      </div>
    )
  }
}

export const CameraModelSelectorConfig = { width: '80vw', title: 'Select Camera Model' }
export default connect(mapStateToProps, { changeCameraModelAction, removeCameraModelAction, resetSpecificManufacturerAndRestartAppAction, updateUiAction })(injectSheet(style)(CameraModelSelector))



// WEBPACK FOOTER //
// ./src/components/CameraModelSelector/index.js